import React, { useState, useCallback, useEffect, useRef } from "react";
import { connect } from "react-redux";
import "../../../om/css/salesorder.css"
import "./adjustAvailableUnavailable.css"
import { GridLayout } from "../../../common/GridLayout";
import { formatQuantity } from "../../../common/Utils";
import {
    Column,
    DataGrid,
    GroupPanel,
    Scrolling,
    Grouping,
    FilterRow,
    FilterPanel,
    Sorting,
    Paging,
    Selection,
    Toolbar,
    Item,
  Export,
  Pager,
  ColumnChooser,
  ColumnChooserSearch,
  StateStoring,
} from "devextreme-react/data-grid";
import GridLayoutManager from "../../../gridLayoutCustomization/GridLayoutManager";
import { Button } from 'devextreme-react/button';
import AdjustAvailableUnAvailableReportMenu from "./AdjustAvailableUnAvailableReportMenu";

/**
 * Selection is deliberately NOT held in this component's state.
 *
 * devextreme-react rebuilds the DataGrid's toolbar <Item> templates on every parent render, which
 * unmounts and remounts their React children. GridLayoutManager fetches GetGridLayouts from a mount
 * effect, so a parent re-render per checkbox click meant a layout fetch per checkbox click. Keeping
 * the selection in a ref + subscriber list means ticking a row re-renders only the two small widgets
 * below, never the parent, so the grid and its toolbar items are left alone.
 */
function useSelectionSubscription(selection) {
    const [rows, setRows] = useState(selection.getRows);
    useEffect(() => selection.subscribe(setRows), [selection]);
    return rows;
}

/** Transfer button — owns its own disabled state so the parent need not re-render. */
const TransferButton = ({ selection, onTransfer }) => {
    const rows = useSelectionSubscription(selection);
    return <Button disabled={rows.length === 0} onClick={onTransfer} text="Transfer" type="default" />;
};

/** Delphi grdTopSelectedCountChange (uWMD0051.PAS:495-520): sums across the whole selection. */
const SelectionTotals = ({ selection }) => {
    const rows = useSelectionSubscription(selection);
    const available = rows.reduce((sum, row) => sum + (Number(row?.available) || 0), 0);
    const unavailable = rows.reduce((sum, row) => sum + (Number(row?.outUnavailable) || 0), 0);
    return (
        <div className="wm-grid-toolbar-totals">
            <span>Total Selected Available: {formatQuantity(available)}</span>
            <span>Total Selected Unavailable: {formatQuantity(unavailable)}</span>
        </div>
    );
};

// Hoisted: rebuilt per render inside the component, these would look like changed options to
// devextreme-react and trigger a needless re-apply/reload of the grid.
const allowedPageSizes = [10, 20, 100, 500, 1000, "all"];
const columnChooserSearchEditorOptions = { placeholder: 'Search column' };

const LocationTrackingSearchResult = (props) => {
    const dataGrid = useRef(null);
    var accessToken = props.oidc.user.access_token;
    const [currentLayout, setCurrentLayout] = useState("Default");

    // Tiny store the toolbar widgets subscribe to. Created once so its identity never changes.
    const selectionRef = useRef(null);
    if (selectionRef.current === null) {
        const listeners = new Set();
        let rows = [];
        selectionRef.current = {
            getRows: () => rows,
            subscribe(fn) {
                listeners.add(fn);
                return () => { listeners.delete(fn); };
            },
            set(next) {
                rows = next || [];
                listeners.forEach((fn) => fn(rows));
            },
        };
    }
    const selection = selectionRef.current;
    const gridKey = "6a4a2946-680f-4925-9af3-bbde3f8aa222";
    // DevExtreme counts selectedRowKeys/focusedRowKey as part of the grid state, so ticking a row
    // checkbox fires customSave and POSTed a layout on every selection change. Selection is transient
    // and is never persisted anyway (GridLayout.sendStorageRequest blanks selectedRowKeys before
    // sending) — so drop those keys and skip the request entirely when nothing else actually changed.
    // Explicit "save layout" still works: GridLayoutManager posts its own state() snapshot.
    const lastSavedStateRef = useRef(null);
    const saveState = useCallback((state) => {
        const { selectedRowKeys, focusedRowKey, selectionFilter, ...persistableState } = state ?? {};
        const serialized = JSON.stringify(persistableState);
        if (serialized === lastSavedStateRef.current) {
            return;
        }
        lastSavedStateRef.current = serialized;
        GridLayout.sendStorageRequest(gridKey, "POST", accessToken, persistableState, currentLayout);
    }, [gridKey, currentLayout, accessToken]);
    // Cache the state read per layout: DevExtreme's state-storing controller can ask for state more
    // than once, and the stored layout cannot have changed in between. A failure clears the cache so
    // the next call retries rather than being wedged on a rejected promise.
    const loadedStateRef = useRef({ layout: null, promise: null });
    const loadState = useCallback(() => {
        if (loadedStateRef.current.layout !== currentLayout || !loadedStateRef.current.promise) {
            const promise = GridLayout
                .sendStorageRequest(gridKey, "GET", accessToken, null, currentLayout)
                .catch((err) => {
                    loadedStateRef.current = { layout: null, promise: null };
                    throw err;
                });
            loadedStateRef.current = { layout: currentLayout, promise };
        }
        return loadedStateRef.current.promise;
    }, [gridKey, currentLayout, accessToken]);
    // Publishes to the subscriber list instead of calling setState — see the note above the widgets.
    const onSelectionChanged = useCallback(({ selectedRowsData }) => {
        selection.set(selectedRowsData);
    }, [selection]);

    // Delphi's ShowDataSelector clears the selection and resets the two captions to 0.00 after a
    // successful Find. The host drives that explicitly — 1) unselect, 2) reset totals, 3) refresh —
    // rather than it being a side effect of the data source changing, so the order is deterministic
    // and reads in the sequence it happens. Published through a plain prop rather than a forwarded
    // ref because this component is wrapped in connect().
    useEffect(() => {
        if (!props.gridActions) {
            return undefined;
        }
        props.gridActions.current.clearSelectionAndResetTotals = () => {
            if (dataGrid.current) {
                dataGrid.current.instance().clearSelection();
            }
            selection.set([]);
        };
        return () => { props.gridActions.current = {}; };
    }, [props.gridActions, selection]);

    const handelTransfer = useCallback(() => {
        props.openPopup(selection.getRows());
    }, [selection, props.openPopup]);
    return (
        <div>
            <DataGrid

onExporting={(e) => {
          if (window.handleDevExtremeDataGridExport) {
            window.handleDevExtremeDataGridExport(e);
          }
        }}
                ref={dataGrid}
                dataSource={props.data}
                allowColumnReordering={true}
                allowColumnResizing={true}
                showBorders={true}
                hoverStateEnabled={true}
                className={props.searchPanelStatus ? "GridHeightAccordianOpen" : "GridHeightAccordianClose"}
                remoteOperations={true}
                searchEnabled={true}
                columnAutoWidth={true}
                onSelectionChanged={onSelectionChanged}
            >
                <ColumnChooser
                    enabled={true}
                    title="Choose Column"
                    width={290}
                    height={290}
                    sortOrder="asc"
                >
                    <ColumnChooserSearch
                        enabled={true}
                        editorOptions={columnChooserSearchEditorOptions} />
                </ColumnChooser>
                <Export enabled={true} allowExportSelectedData={true} />
                {/* allPages matches Delphi Ctrl+A / right-click "Select All", which selects the whole
                    result set rather than the visible page (uWMD0051.PAS:525-531). */}
                <Selection mode="multiple" selectAllMode="allPages" showCheckBoxesMode="always" />
                <Sorting visible={true} mode="multiple" />
                <Paging defaultPageSize={10} />
                <Pager
                    visible={true}
                    showPageSizeSelector={true}
                    allowedPageSizes={allowedPageSizes}
                    showNavigationButtons={true}
                />
                <FilterRow visible={true} />
                <FilterPanel visible={true} />
                <GroupPanel visible={true} />
                <Grouping autoExpandAll={false} />
                <StateStoring
                    enabled={true}
                    type="custom"
                    customLoad={loadState}
                    storageKey={gridKey}
                    customSave={saveState}
                />
                <Scrolling mode="standard" />
                <Column dataField="recordNumber" caption='Record #' allowEditing={false} ></Column>
                <Column dataField="warehouse" caption='Warehouse' ></Column>
                <Column dataField="locationCode" caption='Location' ></Column>
                <Column dataField="itemCode" caption='Item' ></Column>
                <Column dataField="lotCode" caption='Lot' ></Column>
                <Column dataField="subLotCode" caption='SubLot' ></Column>
                <Column dataField="unitCode" caption='Unit' ></Column>
                <Column dataField="available" caption='Available' dataType="number" format="#,##0.00" alignment="right" ></Column>
                <Column dataField="outUnavailable" caption='Unavailable' dataType="number" format="#,##0.00" alignment="right" ></Column>
                <Column dataField="inTransactionNumber" caption='Lot First Receipt Transaction' ></Column>
                <Column dataField="customer" caption='Customer' ></Column>
                <Column dataField="customerName" caption='Customer Name' ></Column>
                {/* Audit stamps — hidden in the Delphi grid too, surfaced via the column chooser. */}
                <Column dataField="creationDateTime" caption='Creation Date Time' dataType="datetime" visible={false} ></Column>
                <Column dataField="creationUserId" caption='Creation User' visible={false} ></Column>
                <Column dataField="lastUpdateDateTime" caption='Last Update Date' dataType="datetime" visible={false} ></Column>
                <Column dataField="lastUpdateUserId" caption='Last Update User' visible={false} ></Column>
                {/* Lot user-defined columns (Delphi DEV-5044). Hidden by default to keep the
                    default layout readable; users surface the ones they need via the column
                    chooser, and StateStoring remembers the choice. */}
                <Column dataField="lotUserDefinedCode1" caption='Lot Code - 1' visible={false} ></Column>
                <Column dataField="lotUserDefinedCode2" caption='Lot Code - 2' visible={false} ></Column>
                <Column dataField="lotUserDefinedCode3" caption='Lot Code - 3' visible={false} ></Column>
                <Column dataField="lotUserDefinedCode4" caption='Lot Code - 4' visible={false} ></Column>
                <Column dataField="lotUserDefinedCode5" caption='Lot Code - 5' visible={false} ></Column>
                <Column dataField="lotUserDefinedCode6" caption='Lot Code - 6' visible={false} ></Column>
                <Column dataField="lotUserDefinedCode7" caption='Lot Code - 7' visible={false} ></Column>
                <Column dataField="lotUserDefinedCode8" caption='Lot Code - 8' visible={false} ></Column>
                <Column dataField="lotUserDefinedCode9" caption='Lot Code - 9' visible={false} ></Column>
                <Column dataField="lotUserDefinedCode10" caption='Lot Code - 10' visible={false} ></Column>
                <Column dataField="lotUserDefinedReference1" caption='Lot Reference - 1' visible={false} ></Column>
                <Column dataField="lotUserDefinedReference2" caption='Lot Reference - 2' visible={false} ></Column>
                <Column dataField="lotUserDefinedReference3" caption='Lot Reference - 3' visible={false} ></Column>
                <Column dataField="lotUserDefinedReference4" caption='Lot Reference - 4' visible={false} ></Column>
                <Column dataField="lotUserDefinedReference5" caption='Lot Reference - 5' visible={false} ></Column>
                <Column dataField="lotUserDefinedReference6" caption='Lot Reference - 6' visible={false} ></Column>
                <Column dataField="lotUserDefinedReference7" caption='Lot Reference - 7' visible={false} ></Column>
                <Column dataField="lotUserDefinedReference8" caption='Lot Reference - 8' visible={false} ></Column>
                <Column dataField="lotUserDefinedReference9" caption='Lot Reference - 9' visible={false} ></Column>
                <Column dataField="lotUserDefinedReference10" caption='Lot Reference - 10' visible={false} ></Column>
                <Column dataField="lotUserDefinedDate1" caption='Lot Date - 1' dataType="date" visible={false} ></Column>
                <Column dataField="lotUserDefinedDate2" caption='Lot Date - 2' dataType="date" visible={false} ></Column>
                <Column dataField="lotUserDefinedDate3" caption='Lot Date - 3' dataType="date" visible={false} ></Column>
                <Column dataField="lotUserDefinedDate4" caption='Lot Date - 4' dataType="date" visible={false} ></Column>
                <Column dataField="lotUserDefinedDate5" caption='Lot Date - 5' dataType="date" visible={false} ></Column>
                <Column dataField="lotUserDefinedDate6" caption='Lot Date - 6' dataType="date" visible={false} ></Column>
                <Column dataField="lotUserDefinedDate7" caption='Lot Date - 7' dataType="date" visible={false} ></Column>
                <Column dataField="lotUserDefinedDate8" caption='Lot Date - 8' dataType="date" visible={false} ></Column>
                <Column dataField="lotUserDefinedDate9" caption='Lot Date - 9' dataType="date" visible={false} ></Column>
                <Column dataField="lotUserDefinedDate10" caption='Lot Date - 10' dataType="date" visible={false} ></Column>
                <Column dataField="lotUserDefinedQuantity1" caption='Lot Quantity - 1' dataType="number" format="#,##0.00" alignment="right" visible={false} ></Column>
                <Column dataField="lotUserDefinedQuantity2" caption='Lot Quantity - 2' dataType="number" format="#,##0.00" alignment="right" visible={false} ></Column>
                <Column dataField="lotUserDefinedQuantity3" caption='Lot Quantity - 3' dataType="number" format="#,##0.00" alignment="right" visible={false} ></Column>
                <Column dataField="lotUserDefinedQuantity4" caption='Lot Quantity - 4' dataType="number" format="#,##0.00" alignment="right" visible={false} ></Column>
                <Column dataField="lotUserDefinedQuantity5" caption='Lot Quantity - 5' dataType="number" format="#,##0.00" alignment="right" visible={false} ></Column>
                <Column dataField="lotUserDefinedQuantity6" caption='Lot Quantity - 6' dataType="number" format="#,##0.00" alignment="right" visible={false} ></Column>
                <Column dataField="lotUserDefinedQuantity7" caption='Lot Quantity - 7' dataType="number" format="#,##0.00" alignment="right" visible={false} ></Column>
                <Column dataField="lotUserDefinedQuantity8" caption='Lot Quantity - 8' dataType="number" format="#,##0.00" alignment="right" visible={false} ></Column>
                <Column dataField="lotUserDefinedQuantity9" caption='Lot Quantity - 9' dataType="number" format="#,##0.00" alignment="right" visible={false} ></Column>
                <Column dataField="lotUserDefinedQuantity10" caption='Lot Quantity - 10' dataType="number" format="#,##0.00" alignment="right" visible={false} ></Column>
                <Toolbar>
                    <Item location="before">
                        <TransferButton selection={selection} onTransfer={handelTransfer} />
                    </Item>
                    <Item location="before">
                        <AdjustAvailableUnAvailableReportMenu />
                    </Item>
                    <Item location="after">
                        {/* In the toolbar rather than under the grid: the GridHeightAccordian* classes
                            size the grid to the full viewport, so a footer below it is clipped. */}
                        <SelectionTotals selection={selection} />
                    </Item>
                    <Item location="after">
                        <GridLayoutManager gridKey={gridKey} dataGrid={dataGrid} setCurrentLayout={setCurrentLayout} />
                    </Item>
                    <Item name="exportButton" />
                    <Item name="columnChooserButton" />
                </Toolbar>
            </DataGrid>
        </div>
    )
};
function mapStateToProps(state) {
    return {
        ReceiptReducer: state.ReceiptReducer,
        oidc: state.oidc,
    };
}
export default connect(mapStateToProps)(LocationTrackingSearchResult);
